import React from 'react';
import PropTypes from 'prop-types';
import { Spacing, Text } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import { buildZoneMap, groupSeatsByZone } from 'utils/seatPricing';

/**
 * SeatsListByZone component.
 * Renders selected seats grouped by zone with subtotals and total (listado por zona).
 *
 * @param {object} props - selectedSeats, seatPriceZones, showTotal.
 * @param {Array<object>} props.selectedSeats - Selected seat objects (with zone and price).
 * @param {Array<object>} [props.seatPriceZones=[]] - Ordered zones from trip details response.
 * @param {boolean} [props.showTotal=true] - Whether to show the total row.
 * @returns {JSX.Element} The rendered list by zone.
 */
function SeatsListByZone({ selectedSeats, seatPriceZones = [], showTotal = true }) {
  const zoneMap = buildZoneMap({ seatPriceZones });
  const { groups, total } = groupSeatsByZone(selectedSeats || [], { zoneMap });

  if (groups.length === 0) return null;

  return (
    <Spacing size="XS" vertical>
      {groups.map(({ zoneKey, label, count, subtotal, color }) => (
        <div
          key={zoneKey}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 8,
                height: 8,
                minWidth: 8,
                borderRadius: '50%',
                backgroundColor: color || '#9B9B9B',
              }}
            />
            <Text size="S" color="grayLight" weight="regular">
              {`${label} - ${count}`}
            </Text>
          </div>
          <LocalizedCurrency price={subtotal} currencyStyle="currency" size="S" color="grayLight" />
        </div>
      ))}
      {showTotal && (
        <Spacing size="XS" vertical alignItems="flex-end">
          <Text size="S" weight="bold">
            <LocalizedCurrency price={total} currencyStyle="currency" />
          </Text>
        </Spacing>
      )}
    </Spacing>
  );
}

SeatsListByZone.propTypes = {
  selectedSeats: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      seatFareId: PropTypes.string,
      seatFareDescription: PropTypes.string,
      seatFareReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      isPickedAsAdjacent: PropTypes.bool,
    }),
  ),
  seatPriceZones: PropTypes.arrayOf(
    PropTypes.shape({
      seatFareReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  showTotal: PropTypes.bool,
};

SeatsListByZone.defaultProps = {
  selectedSeats: [],
  seatPriceZones: [],
  showTotal: true,
};

export default SeatsListByZone;
